import { useCallback, useEffect, useRef, useState } from 'react';
import { parseSSE } from '../api/sse';
import { apiGet, apiJson } from '../api/client';
import type { Bite, SwatEntry } from '../api/gadfly';
import {
  applyGadflyBites,
  applyGadflySwat,
  applyGadflyTurnEnd,
} from './useGadflyTurn';
import type { OrchestratorState } from './useOrchestrator';
import { isMuted, playAudio } from './useAudioPlayer';
import type { ToolOutput, ToolStatus } from '../components/ToolCard';
import { speakKokoro } from '../zerovoice/speakKokoro';
import { speakKokoroBlend } from '../zerovoice/speakKokoroBlend';
import type { PlaybackToken } from '../zerovoice/zeroTTSPlayer';
import type {
  KokoroAgentGender,
  KokoroBlendMode,
  KokoroGender,
  TTSProvider,
  VoiceSpec,
} from '../zerovoice/types';
import {
  resolveSpeechText,
  type SpeechFallback,
} from './speechResolver';

export interface GroundingCitation {
  title?: string;
  uri?: string;
  snippet?: string;
  domain?: string;
}

export interface GroundingInfo {
  query?: string;
  citations: GroundingCitation[];
}

export interface MoodPoint {
  v: number;
  a: number;
}

export interface MoodInfo {
  /** Nearest-centroid label. Human-readable display name. */
  label: string;
  /** Current accumulator state on the circumplex, after this turn. */
  valence: number;
  arousal: number;
  /** Agent's deterministic cold-start point. */
  baseline: MoodPoint;
  /** What the model emitted this turn. Null when tag was missing. */
  signal: MoodPoint | null;
  /** Where the final state came from this turn. */
  signalSource: 'model' | 'held' | 'baseline';
  /** Smoothing weight applied to produce the new state. */
  alpha?: number;
  /** Procedural directive text sampled this turn. */
  directive?: string;
}

export interface ChatAttachment {
  signedUrl: string;
  mimeType: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  error?: string;
  koji?: string;
  audioUrl?: string;
  toolOutputs?: ToolOutput[];
  grounding?: GroundingInfo;
  mood?: MoodInfo;
  attachment?: ChatAttachment;
  /** Stage 9: the <speech>…</speech> summary the model emitted, if any.
   *  Never rendered into the transcript body — only used for TTS and as
   *  an affordance badge. */
  speechText?: string;
}

interface UseChatOpts {
  onStateChange?: (state: OrchestratorState) => void;
  model?: string;
  /** Voice to request for TTS — usually the active persona's voice. */
  voice?: string;
  /** Whether to auto-synthesise assistant replies. */
  speakResponses?: boolean;
  // --- Stage 7: ZeroVoice (Kokoro) ---
  /** TTS provider. 'gemini' uses the worker endpoint; 'kokoro' runs locally. */
  ttsProvider?: TTSProvider;
  /** Agent name whose voice should be used when ttsProvider === 'kokoro'. */
  kokoroAgentName?: string;
  /** Per-agent gender override, or 'default' to inherit kokoroDefaultGender. */
  kokoroAgentGender?: KokoroAgentGender;
  /** Gender pool used when no per-agent override. */
  kokoroDefaultGender?: KokoroGender;
  /** Pinned voice id for the active agent, if any. */
  kokoroPinnedVoiceId?: string | null;
  /** 16-char hex world seed. */
  kokoroWorldSeed?: string;
  /** If true, fall back to Gemini when Kokoro synthesis fails. */
  kokoroFallbackToGemini?: boolean;
  /** Stage 8: 'single' uses Stage 7 one-voice pick, 'blend' uses the full
   *  deterministic blend engine. */
  kokoroBlendMode?: KokoroBlendMode;
  /** Stage 8: pinned VoiceSpec for the active agent (blend-mode pin), if any. */
  kokoroPinnedSpec?: VoiceSpec | null;
  /** Stage 9: when true, speak the <speech>…</speech> summary instead of
   *  the full reply (or fall back per speechFallback). Default true. */
  speechSummaryEnabled?: boolean;
  /** Stage 9: what to speak when the model omits <speech>. */
  speechFallback?: SpeechFallback;
  /** Stage 26: ZeroTTS instant-filler cache. When provided, the chat fires
   *  a cached ack filler at message submit and a tool-invoke filler on
   *  tool-start SSE events, then cuts the filler via stopActive() the
   *  moment the real Kokoro/Gemini audio is ready to play. */
  zeroTTS?: {
    speak: (
      contextName: string,
      payload?: Record<string, string>,
    ) => Promise<PlaybackToken>;
    stopActive: () => void;
    enabledContexts: {
      ack: boolean;
      tool_invoke: boolean;
      progress: boolean;
      error: boolean;
    };
  };
}

interface SsePayload {
  type:
    | 'text'
    | 'done'
    | 'error'
    | 'tool-start'
    | 'tool-end'
    | 'tool-error'
    | 'grounding'
    | 'mood'
    | 'speech-summary'
    // Stage 50: Gadfly events. Fired between user-message processing and
    // the assistant generation (`gadfly_bites`) and any time the persona
    // calls gadfly_swat during the turn (`gadfly_swat`). Both are
    // operator-inspector signals — they never affect chat rendering.
    | 'gadfly_bites'
    | 'gadfly_swat';
  text?: string;
  error?: string;
  conversationId?: string;
  turnId?: string;
  koji?: string;
  fullText?: string;
  // Tool event fields
  id?: string;
  name?: string;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
  info?: GroundingInfo;
  // Mood event fields (Stage 5)
  label?: string;
  valence?: number;
  arousal?: number;
  baseline?: MoodPoint;
  signal?: MoodPoint | null;
  signalSource?: 'model' | 'held' | 'baseline';
  alpha?: number;
  directive?: string;
  // Stage 50: Gadfly event payloads.
  bites?: Bite[];
  failed?: boolean;
  failureReason?: string | null;
  generatedAt?: string;
  durationMs?: number;
  stripeRatioApplied?: { keep: number; cut: number };
  modelUsed?: string;
  entry?: SwatEntry;
}

interface SpeakResponse {
  id: string;
  r2Key: string;
  signedUrl: string;
  sizeBytes: number;
  durationMs: number;
  voice: string;
  model: string;
}

// ── Stage 11b + 12 (B1,B2): conversation-hydration shape ───────────────
interface HydratedTurn {
  id: string;
  turn_index: number;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_calls: string | null;
  tool_name: string | null;
  tool_result: string | null;
  koji_codename: string | null;
  model_used: string | null;
  created_at: number;
  mood_valence: number | null;
  mood_arousal: number | null;
  mood_signal_valence: number | null;
  mood_signal_arousal: number | null;
  mood_label: string | null;
  audio_signed_url: string | null;
  attachment: { r2Key: string; signedUrl: string; mimeType: string } | null;
  // Stage 12 (B1): parsed `grounding` object, or null.
  grounding: unknown;
  // Stage 12 (B2): stored <speech>…</speech> summary text, or null.
  speech_summary: string | null;
}

interface ConversationGetResponse {
  conversation: { id: string };
  turns: HydratedTurn[];
}

function parseJsonSafe(s: string | null): Record<string, unknown> | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Stage 11b: rebuild a list of ChatMessage instances from stored turns.
 * Tool rows fold into the preceding assistant's toolOutputs[]. Orphan tool
 * rows are dropped with a console.warn — they indicate a prior broken
 * stream, not a hydration bug.
 */
function hydrateTurnsToMessages(turns: HydratedTurn[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const t of turns) {
    if (t.role === 'user') {
      out.push({
        id: t.id,
        role: 'user',
        text: t.content,
        attachment: t.attachment
          ? {
              signedUrl: t.attachment.signedUrl,
              mimeType: t.attachment.mimeType,
            }
          : undefined,
      });
    } else if (t.role === 'assistant') {
      const msg: ChatMessage = {
        id: t.id,
        role: 'assistant',
        text: t.content,
        koji: t.koji_codename ?? undefined,
        audioUrl: t.audio_signed_url ?? undefined,
      };
      if (
        t.mood_valence != null &&
        t.mood_arousal != null &&
        t.mood_label != null
      ) {
        msg.mood = {
          label: t.mood_label,
          valence: t.mood_valence,
          arousal: t.mood_arousal,
          // Baseline was dropped in migration 018; render a neutral stub so
          // the existing MoodInfo type stays satisfied.
          baseline: { v: 0, a: 0 },
          signal:
            t.mood_signal_valence != null && t.mood_signal_arousal != null
              ? { v: t.mood_signal_valence, a: t.mood_signal_arousal }
              : null,
          signalSource: t.mood_signal_valence != null ? 'model' : 'held',
        };
      }
      // Stage 12 (B1): restore grounding citations when stored.
      if (t.grounding && typeof t.grounding === 'object') {
        const g = t.grounding as {
          query?: unknown;
          citations?: unknown;
        };
        if (Array.isArray(g.citations)) {
          msg.grounding = {
            query: typeof g.query === 'string' ? g.query : undefined,
            citations: (g.citations as Array<Record<string, unknown>>).map(
              (c) => ({
                title: typeof c.title === 'string' ? c.title : undefined,
                uri: typeof c.uri === 'string' ? c.uri : undefined,
                snippet:
                  typeof c.snippet === 'string' ? c.snippet : undefined,
                domain: typeof c.domain === 'string' ? c.domain : undefined,
              }),
            ),
          };
        }
      }
      // Stage 12 (B2): restore the 🔊 N chars badge via speechText.
      if (t.speech_summary) msg.speechText = t.speech_summary;
      out.push(msg);
    } else if (t.role === 'tool') {
      const last = out[out.length - 1];
      if (last && last.role === 'assistant') {
        const res = parseJsonSafe(t.tool_result);
        const tool: ToolOutput = {
          id: t.id,
          name: t.tool_name ?? 'tool',
          status: 'done' as ToolStatus,
          result: res ?? undefined,
        };
        last.toolOutputs = [...(last.toolOutputs ?? []), tool];
      } else {
        console.warn('[hydrate] orphan tool turn dropped:', t.id);
      }
    }
  }
  return out;
}

const API_BASE = import.meta.env.VITE_API_BASE as string;

// Stage 26: convert a tool name like "gpt_image_gen" or "web-search" into a
// human-readable phrase matching the LAINA profile's tool_name pool values
// ("gpt image gen", "web search"). Falls back to the raw name if already
// human-shaped.
function humanizeToolName(raw: string): string {
  return raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function useChat(opts: UseChatOpts = {}) {
  const {
    onStateChange,
    model,
    voice,
    speakResponses = true,
    ttsProvider = 'gemini',
    kokoroAgentName = '',
    kokoroAgentGender = 'default',
    kokoroDefaultGender = 'mixed',
    kokoroPinnedVoiceId = null,
    kokoroWorldSeed = 'DEADBEEFC0FFEE',
    kokoroFallbackToGemini = true,
    kokoroBlendMode = 'single',
    kokoroPinnedSpec = null,
    speechSummaryEnabled = true,
    speechFallback = 'first-sentence',
    zeroTTS,
  } = opts;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const conversationIdRef = useRef<string | null>(null);
  const speakResponsesRef = useRef(speakResponses);
  const voiceRef = useRef(voice);
  const providerRef = useRef<TTSProvider>(ttsProvider);
  const kokoroAgentNameRef = useRef(kokoroAgentName);
  const kokoroAgentGenderRef = useRef<KokoroAgentGender>(kokoroAgentGender);
  const kokoroDefaultGenderRef = useRef<KokoroGender>(kokoroDefaultGender);
  const kokoroPinRef = useRef<string | null>(kokoroPinnedVoiceId);
  const kokoroSeedRef = useRef(kokoroWorldSeed);
  const kokoroFallbackRef = useRef(kokoroFallbackToGemini);
  const kokoroBlendModeRef = useRef<KokoroBlendMode>(kokoroBlendMode);
  const kokoroPinnedSpecRef = useRef<VoiceSpec | null>(kokoroPinnedSpec);
  const speechSummaryEnabledRef = useRef<boolean>(speechSummaryEnabled);
  const speechFallbackRef = useRef<SpeechFallback>(speechFallback);
  // Stage 26: hold the latest ZeroTTS handle in a ref so the async SSE
  // handlers inside sendMessage always see the current value.
  const zeroTTSRef = useRef<UseChatOpts['zeroTTS']>(zeroTTS);

  useEffect(() => {
    speakResponsesRef.current = speakResponses;
  }, [speakResponses]);
  useEffect(() => {
    voiceRef.current = voice;
  }, [voice]);
  useEffect(() => {
    providerRef.current = ttsProvider;
  }, [ttsProvider]);
  useEffect(() => {
    kokoroAgentNameRef.current = kokoroAgentName;
  }, [kokoroAgentName]);
  useEffect(() => {
    kokoroAgentGenderRef.current = kokoroAgentGender;
  }, [kokoroAgentGender]);
  useEffect(() => {
    kokoroDefaultGenderRef.current = kokoroDefaultGender;
  }, [kokoroDefaultGender]);
  useEffect(() => {
    kokoroPinRef.current = kokoroPinnedVoiceId;
  }, [kokoroPinnedVoiceId]);
  useEffect(() => {
    kokoroSeedRef.current = kokoroWorldSeed;
  }, [kokoroWorldSeed]);
  useEffect(() => {
    kokoroFallbackRef.current = kokoroFallbackToGemini;
  }, [kokoroFallbackToGemini]);
  useEffect(() => {
    kokoroBlendModeRef.current = kokoroBlendMode;
  }, [kokoroBlendMode]);
  useEffect(() => {
    kokoroPinnedSpecRef.current = kokoroPinnedSpec;
  }, [kokoroPinnedSpec]);
  useEffect(() => {
    speechSummaryEnabledRef.current = speechSummaryEnabled;
  }, [speechSummaryEnabled]);
  useEffect(() => {
    speechFallbackRef.current = speechFallback;
  }, [speechFallback]);
  useEffect(() => {
    zeroTTSRef.current = zeroTTS;
  }, [zeroTTS]);

  const speakText = useCallback(
    async (
      assistantMsgId: string,
      text: string,
      turnId: string | null,
      // Stage 12 (E2): optional mood point for Kokoro blend-mode modulation.
      mood: MoodPoint | null = null,
    ) => {
      if (!speakResponsesRef.current) return;
      if (isMuted()) return;
      const trimmed = text.trim();
      if (!trimmed) return;

      setSpeaking(true);
      onStateChange?.('speaking');

      const geminiPath = async (): Promise<SpeakResponse> => {
        const payload: { text: string; voice?: string; turnId?: string } = {
          text: trimmed,
        };
        if (voiceRef.current) payload.voice = voiceRef.current;
        if (turnId) payload.turnId = turnId;
        return apiJson<SpeakResponse>('POST', '/api/voice/speak', payload);
      };

      const kokoroPath = async (): Promise<SpeakResponse> => {
        if (kokoroBlendModeRef.current === 'blend') {
          return speakKokoroBlend({
            text: trimmed,
            turnId: turnId ?? null,
            agentName: kokoroAgentNameRef.current || 'unknown',
            agentGender: kokoroAgentGenderRef.current,
            defaultGender: kokoroDefaultGenderRef.current,
            pinnedSpec: kokoroPinnedSpecRef.current,
            worldSeedHex: kokoroSeedRef.current,
            // Stage 12 (E2): gentle mood-aware modulation.
            moodModulation: mood
              ? { valence: mood.v, arousal: mood.a }
              : null,
          });
        }
        return speakKokoro({
          text: trimmed,
          turnId: turnId ?? null,
          agentName: kokoroAgentNameRef.current || 'unknown',
          agentGender: kokoroAgentGenderRef.current,
          defaultGender: kokoroDefaultGenderRef.current,
          pinnedVoiceId: kokoroPinRef.current,
          worldSeedHex: kokoroSeedRef.current,
        });
      };

      try {
        let res: SpeakResponse;
        if (providerRef.current === 'kokoro') {
          try {
            res = await kokoroPath();
          } catch (err) {
            console.warn('Kokoro TTS failed:', err);
            if (!kokoroFallbackRef.current) throw err;
            res = await geminiPath();
          }
        } else {
          res = await geminiPath();
        }

        const absolute = res.signedUrl.startsWith('http')
          ? res.signedUrl
          : `${API_BASE}${res.signedUrl}`;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, audioUrl: absolute } : m,
          ),
        );
        // Stage 26: real audio is ready — cut the ZeroTTS filler hard so
        // the cached phrase does not bleed into the start of the reply.
        zeroTTSRef.current?.stopActive();
        await playAudio(absolute);
      } catch (err) {
        console.warn('TTS failed:', err);
      } finally {
        setSpeaking(false);
        onStateChange?.('idle');
      }
    },
    [onStateChange],
  );

  const sendMessage = useCallback(
    async (
      text: string,
      attachment?: { r2Key: string; signedUrl: string; mimeType: string },
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !attachment) return;
      if (busy) return;

      // Compose the wire-text: prefix with the upload marker so the
      // assistant sees the r2Key in its user-turn context and can pass it
      // to describe_image reliably.
      const userCaption = trimmed || (attachment ? 'Please describe this image.' : '');
      const wireText = attachment
        ? `[Image uploaded: ${attachment.r2Key}] ${userCaption}`
        : trimmed;

      setBusy(true);
      onStateChange?.('thinking');

      // Stage 26: fire an instant cached "ack" filler the moment the user
      // submits. This gives the assistant an audible presence before the
      // SSE stream even opens. The filler is cut off later by stopActive()
      // when the real reply audio is ready to play.
      const zt = zeroTTSRef.current;
      if (zt?.enabledContexts.ack && !isMuted()) {
        try {
          void zt.speak('ack', {});
        } catch (err) {
          console.warn('[zerotts] ack speak failed:', err);
        }
      }

      // The user-visible bubble keeps the user's own text PLUS the
      // attachment thumbnail — it does NOT show the r2Key marker, which
      // is an implementation detail of the model-side context.
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        text: userCaption,
        attachment: attachment
          ? {
              signedUrl: attachment.signedUrl,
              mimeType: attachment.mimeType,
            }
          : undefined,
      };
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: '',
        streaming: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      let finalText = '';
      let finalTurnId: string | null = null;
      // Stage 9: filled when the worker emits a `speech-summary` event.
      // Reset per-turn; the ref outer-scope isn't required because each
      // sendMessage runs serially under `busy`.
      const speechSummaryCapture: { current: string | null } = { current: null };
      // Stage 12 (E2): capture this turn's mood point for optional
      // modulation in the Kokoro blend path.
      const moodCapture: { current: MoodPoint | null } = { current: null };
      // Stage 12 (E1): when the speech-summary SSE event fires mid-stream
      // we kick off Kokoro synth immediately for latency; this flag
      // prevents the done-tail path from re-synthesising the same text.
      const eagerSynthStarted: { current: boolean } = { current: false };
      let hadError = false;

      try {
        for await (const frame of parseSSE('/api/assistant/chat', {
          method: 'POST',
          body: {
            conversationId: conversationIdRef.current,
            text: wireText,
            model,
          },
        })) {
          let payload: SsePayload;
          try {
            payload = JSON.parse(frame.data) as SsePayload;
          } catch {
            continue;
          }

          if (payload.type === 'text' && typeof payload.text === 'string') {
            const delta = payload.text;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, text: m.text + delta }
                  : m,
              ),
            );
          } else if (
            payload.type === 'mood' &&
            typeof payload.label === 'string' &&
            typeof payload.valence === 'number' &&
            typeof payload.arousal === 'number' &&
            payload.baseline
          ) {
            const info: MoodInfo = {
              label: payload.label,
              valence: payload.valence,
              arousal: payload.arousal,
              baseline: payload.baseline,
              signal: payload.signal ?? null,
              signalSource: payload.signalSource ?? 'held',
              alpha: payload.alpha,
              directive: payload.directive,
            };
            // Stage 12 (E2): capture mood for TTS modulation.
            moodCapture.current = {
              v: payload.valence,
              a: payload.arousal,
            };
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id ? { ...m, mood: info } : m,
              ),
            );
          } else if (payload.type === 'grounding' && payload.info) {
            const info = payload.info;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id ? { ...m, grounding: info } : m,
              ),
            );
          } else if (
            payload.type === 'speech-summary' &&
            typeof payload.text === 'string'
          ) {
            // Stage 9: the worker extracted the <speech>…</speech> content
            // and forwarded it. Stash for the post-stream TTS routing and
            // attach to the bubble for the 🔊 badge.
            const summary = payload.text.trim();
            speechSummaryCapture.current = summary.length > 0 ? summary : null;
            if (summary.length > 0) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id ? { ...m, speechText: summary } : m,
                ),
              );
              // Stage 12 (E1): start synth eagerly the moment the summary
              // arrives — don't wait for the `done` frame. Shaves roughly
              // the mood/grounding/done round-trip off audio-start time.
              if (
                !eagerSynthStarted.current &&
                speakResponsesRef.current &&
                !isMuted() &&
                speechSummaryEnabledRef.current
              ) {
                eagerSynthStarted.current = true;
                void speakText(
                  assistantMsg.id,
                  summary,
                  payload.turnId ?? null,
                  moodCapture.current,
                );
              }
            }
          } else if (payload.type === 'tool-start' && payload.id && payload.name) {
            // Stage 26: swap the ack filler (or previous filler) for a
            // tool-specific cached phrase. speak() is fire-and-forget —
            // errors only log, they never break the SSE loop.
            const ztTool = zeroTTSRef.current;
            if (ztTool?.enabledContexts.tool_invoke && !isMuted()) {
              try {
                void ztTool.speak('tool_invoke', {
                  tool_name: humanizeToolName(payload.name),
                });
              } catch (err) {
                console.warn('[zerotts] tool_invoke speak failed:', err);
              }
            }
            const running: ToolOutput = {
              id: payload.id,
              name: payload.name,
              status: 'running' as ToolStatus,
              args: payload.args,
            };
            onStateChange?.('thinking');
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? {
                      ...m,
                      toolOutputs: [...(m.toolOutputs ?? []), running],
                    }
                  : m,
              ),
            );
          } else if (payload.type === 'tool-end' && payload.id && payload.name) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantMsg.id) return m;
                const outs = m.toolOutputs ?? [];
                const idx = outs.findIndex((o) => o.id === payload.id);
                const replaced: ToolOutput = {
                  id: payload.id as string,
                  name: payload.name as string,
                  status: 'done' as ToolStatus,
                  args: idx >= 0 ? outs[idx].args : undefined,
                  result: payload.result,
                };
                const next =
                  idx >= 0
                    ? outs.map((o, i) => (i === idx ? replaced : o))
                    : [...outs, replaced];
                return { ...m, toolOutputs: next };
              }),
            );
            onStateChange?.('thinking');
          } else if (
            payload.type === 'tool-error' &&
            payload.id &&
            payload.name
          ) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantMsg.id) return m;
                const outs = m.toolOutputs ?? [];
                const idx = outs.findIndex((o) => o.id === payload.id);
                const replaced: ToolOutput = {
                  id: payload.id as string,
                  name: payload.name as string,
                  status: 'error' as ToolStatus,
                  args: idx >= 0 ? outs[idx].args : undefined,
                  error: payload.error,
                };
                const next =
                  idx >= 0
                    ? outs.map((o, i) => (i === idx ? replaced : o))
                    : [...outs, replaced];
                return { ...m, toolOutputs: next };
              }),
            );
            onStateChange?.('thinking');
          } else if (
            // Stage 50: route the operator-inspector signals into the
            // gadfly live store. Both events are FYI for the inspector
            // pane — the chat rendering layer ignores them entirely.
            payload.type === 'gadfly_bites' &&
            typeof payload.turnId === 'string' &&
            Array.isArray(payload.bites)
          ) {
            applyGadflyBites({
              turnId: payload.turnId,
              bites: payload.bites,
              failed: payload.failed ?? false,
              failureReason: payload.failureReason ?? null,
              generatedAt: payload.generatedAt ?? new Date().toISOString(),
              durationMs: payload.durationMs ?? 0,
              stripeRatioApplied: payload.stripeRatioApplied ?? {
                keep: 1,
                cut: 2,
              },
              modelUsed: payload.modelUsed ?? '(unknown)',
            });
          } else if (
            payload.type === 'gadfly_swat' &&
            typeof payload.turnId === 'string' &&
            payload.entry
          ) {
            applyGadflySwat({ turnId: payload.turnId, entry: payload.entry });
          } else if (payload.type === 'done') {
            if (payload.conversationId)
              conversationIdRef.current = payload.conversationId;
            finalTurnId = payload.turnId ?? null;
            // Stage 97: close the current Gadfly turn so the WARP
            // visualization can render untagged bites as implicit REJECTs.
            if (
              typeof payload.turnId === 'string' &&
              payload.turnId.length > 0
            ) {
              applyGadflyTurnEnd({ turnId: payload.turnId });
            }
            setMessages((prev) => {
              const next = prev.map((m) => {
                if (m.id !== assistantMsg.id) return m;
                const resolved =
                  typeof payload.fullText === 'string' &&
                  payload.fullText.length > 0
                    ? payload.fullText
                    : m.text;
                finalText = resolved;
                return {
                  ...m,
                  streaming: false,
                  text: resolved,
                  koji: payload.koji,
                };
              });
              return next;
            });
          } else if (payload.type === 'error') {
            hadError = true;
            const errMsg = payload.error ?? 'Unknown error';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? {
                      ...m,
                      streaming: false,
                      error: errMsg,
                      text: m.text || `Error: ${errMsg}`,
                    }
                  : m,
              ),
            );
            onStateChange?.('error');
            throw new Error(errMsg);
          }
        }

        if (!hadError) onStateChange?.('idle');
      } catch (err) {
        hadError = true;
        const msg = err instanceof Error ? err.message : String(err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? {
                  ...m,
                  streaming: false,
                  error: msg,
                  text: m.text || `Error: ${msg}`,
                }
              : m,
          ),
        );
        onStateChange?.('error');
      } finally {
        setBusy(false);
      }

      if (!hadError && finalText.trim().length > 0) {
        // Stage 12 (E1): skip when eager synth already started from the
        // `speech-summary` SSE event — that path already spoke the same
        // text and is in flight / done.
        if (eagerSynthStarted.current) {
          // no-op
        } else {
          // Stage 9: prefer the <speech> summary when available, otherwise
          // consult the user's fallback setting. Returns null when nothing
          // should be spoken (e.g. fallback='skip' and no summary).
          const toSpeak = resolveSpeechText({
            fullText: finalText,
            summary: speechSummaryCapture.current,
            enabled: speechSummaryEnabledRef.current,
            fallback: speechFallbackRef.current,
          });
          if (toSpeak) {
            // Fire-and-forget TTS. Orchestrator moves to 'speaking' internally.
            // Stage 12 (E2): pass this turn's captured mood point so Kokoro
            // blend-mode can gently modulate pitch/energy.
            void speakText(
              assistantMsg.id,
              toSpeak,
              finalTurnId,
              moodCapture.current,
            );
          }
        }
      }
    },
    [busy, model, onStateChange, speakText],
  );

  const reset = useCallback(() => {
    conversationIdRef.current = null;
    setMessages([]);
  }, []);

  // ── Stage 11b: conversation hydration ──────────────────────────────
  const [hydrating, setHydrating] = useState(false);
  const [hydrationError, setHydrationError] = useState<string | null>(null);

  const hydrateConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      setHydrating(true);
      setBusy(true);
      setHydrationError(null);
      try {
        const res = await apiGet<ConversationGetResponse>(
          `/api/conversations/${encodeURIComponent(conversationId)}`,
        );
        const msgs = hydrateTurnsToMessages(res.turns ?? []);
        setMessages(msgs);
        conversationIdRef.current = conversationId;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setHydrationError(msg);
        throw err;
      } finally {
        setBusy(false);
        setHydrating(false);
      }
    },
    [],
  );

  return {
    messages,
    busy,
    speaking,
    sendMessage,
    reset,
    // Stage 11b: resume a prior conversation by id.
    hydrating,
    hydrationError,
    hydrateConversation,
    conversationId: conversationIdRef.current,
  };
}
